import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  initializeAnalytics,
  isAnalyticsDebugMode,
  normalizeAnalyticsPath,
  setAnalyticsIdentity,
  trackEvent,
  trackPageView,
} from "../analytics";
import { useAppStore } from "../state";
import { useAuthSession } from "../useAuthSession";

type PerformanceEntryWithValue = PerformanceEntry & { value?: number; hadRecentInput?: boolean };

function metricRating(name: string, value: number) {
  const thresholds: Record<string, [number, number]> = {
    CLS: [0.1, 0.25],
    INP: [200, 500],
    LCP: [2_500, 4_000],
  };
  const threshold = thresholds[name];
  if (!threshold) return "unknown";
  return value <= threshold[0] ? "good" : value <= threshold[1] ? "needs_improvement" : "poor";
}

function observeWebVitals() {
  if (!("PerformanceObserver" in window)) return () => undefined;
  const observers: PerformanceObserver[] = [];
  let cls = 0;
  let inp = 0;
  let lcp = 0;

  const observe = (type: string, callback: (entries: PerformanceEntryList) => void) => {
    try {
      const observer = new PerformanceObserver((list) => callback(list.getEntries()));
      observer.observe({ type, buffered: true });
      observers.push(observer);
    } catch {
      // Older browsers simply omit unsupported metrics.
    }
  };

  observe("layout-shift", (entries) => {
    for (const entry of entries as PerformanceEntryWithValue[])
      if (!entry.hadRecentInput) cls += entry.value ?? 0;
  });
  observe("largest-contentful-paint", (entries) => {
    lcp = entries.at(-1)?.startTime ?? lcp;
  });
  observe("event", (entries) => {
    for (const entry of entries) inp = Math.max(inp, entry.duration);
  });

  const report = () => {
    if (lcp)
      trackEvent("web_vital", {
        metric_name: "LCP",
        metric_value: Math.round(lcp),
        metric_rating: metricRating("LCP", lcp),
      });
    if (cls)
      trackEvent("web_vital", {
        metric_name: "CLS",
        metric_value: Math.round(cls * 1_000),
        metric_rating: metricRating("CLS", cls),
      });
    if (inp)
      trackEvent("web_vital", {
        metric_name: "INP",
        metric_value: Math.round(inp),
        metric_rating: metricRating("INP", inp),
      });
  };
  window.addEventListener("pagehide", report, { once: true });
  return () => {
    window.removeEventListener("pagehide", report);
    for (const observer of observers) observer.disconnect();
  };
}

function AnalyticsRuntime() {
  const location = useLocation();
  const { session } = useAuthSession();
  const guest = useAppStore((state) => state.guest);
  const previousPage = useRef("");
  const diagnosticReported = useRef(false);

  useEffect(() => {
    initializeAnalytics();
    if (isAnalyticsDebugMode() && !diagnosticReported.current) {
      diagnosticReported.current = true;
      trackEvent("analytics_diagnostic", { source: "manual_verification" });
    }
    const reportError = (event: ErrorEvent) =>
      trackEvent("exception", {
        exception_type: event.error?.name || "javascript_error",
        fatal: false,
        route: normalizeAnalyticsPath(window.location.pathname),
      });
    const reportRejection = (event: PromiseRejectionEvent) =>
      trackEvent("exception", {
        exception_type:
          event.reason instanceof Error ? event.reason.name : "unhandled_promise_rejection",
        fatal: false,
        route: normalizeAnalyticsPath(window.location.pathname),
      });
    window.addEventListener("error", reportError);
    window.addEventListener("unhandledrejection", reportRejection);
    const stopVitals = observeWebVitals();
    return () => {
      window.removeEventListener("error", reportError);
      window.removeEventListener("unhandledrejection", reportRejection);
      stopVitals();
    };
  }, []);

  useEffect(() => {
    const page = location.pathname;
    if (previousPage.current === page) return;
    previousPage.current = page;
    window.requestAnimationFrame(() => trackPageView(page));
  }, [location.pathname]);

  useEffect(() => {
    setAnalyticsIdentity(session ? "registered" : guest ? "guest" : "visitor");
  }, [guest, session]);

  return null;
}

export function AnalyticsManager() {
  return <AnalyticsRuntime />;
}
